import {
	normalizeStatusKey,
	normalizeStatusLabel,
} from "../../../../../../../../../shared/utils/normalize-status-label";

export interface SortInfo {
	sortMethod: string;
	sortBy: string;
}

export interface PreparedCampaign {
	readonly campaign_id?: string | number;
	readonly campaign_id_raw?: string | number;
	readonly campaign_name?: string;
	readonly account_name?: string;
	readonly account_id?: string;
	readonly app_id?: string;
	readonly campaign_status?: string | number;
	readonly cost_usd?: unknown;
	readonly _statusKey: string;
	readonly _statusRank: number | undefined;
	readonly _statusLabel: string;
	readonly _searchFields: readonly string[];
	readonly _campaignIdNum: number;
	readonly _costNum: number;
	readonly _isNew: boolean;
}

const STATUS_PRIORITY: Record<string, number> = {
	ENABLED: 0,
	PAUSED: 1,
	PARTIALLY_REJECTED: 2,
	REJECTED: 3,
	SUSPENDED: 4,
	REMOVED: 5,
};

const getCampaignStatusPriorityRank = (
	raw: string | number | null | undefined,
): number | undefined => {
	const label = normalizeStatusLabel(raw);
	const mapKey = label.replace(/\s+/g, "_");
	return STATUS_PRIORITY[mapKey];
};

const normalizeSearchField = (value: unknown): string =>
	String(value ?? "")
		.toLowerCase()
		.replace(/[-\s]/g, "");

const normalizeSearchQuery = (searchQuery: string): string =>
	searchQuery.trim().toLowerCase().replace(/[-\s]/g, "");

export const prepareCampaigns = (
	data: readonly Record<string, unknown>[] | undefined,
): PreparedCampaign[] =>
	(data ?? []).map((item) => {
		const statusLabel = normalizeStatusLabel(
			item.campaign_status as string | number | null | undefined,
		);

		return {
			...item,
			_statusKey: normalizeStatusKey(
				item.campaign_status as string | number | null | undefined,
			),
			_statusRank: getCampaignStatusPriorityRank(
				item.campaign_status as string | number | null | undefined,
			),
			_statusLabel: statusLabel,
			_searchFields: [
				normalizeSearchField(item.campaign_name),
				normalizeSearchField(item.campaign_id),
				normalizeSearchField(item.account_name),
				normalizeSearchField(item.account_id),
				normalizeSearchField(item.app_id),
			],
			_campaignIdNum:
				parseInt(
					String(item.campaign_id ?? item.campaign_id_raw ?? "0"),
					10,
				) || 0,
			_costNum: parseFloat(String(item.cost_usd)) || 0,
			_isNew: Boolean((item as Record<string, unknown>).new),
		};
	});

export const buildCampaignsByStatus = (
	prepared: readonly PreparedCampaign[],
): Map<string, PreparedCampaign[]> => {
	const byStatus = new Map<string, PreparedCampaign[]>();

	for (const item of prepared) {
		const list = byStatus.get(item._statusKey);
		if (list) {
			list.push(item);
		} else {
			byStatus.set(item._statusKey, [item]);
		}
	}

	return byStatus;
};

export const buildPreparedCampaignsIndex = (
	data: readonly Record<string, unknown>[] | undefined,
): {
	prepared: PreparedCampaign[];
	byStatus: Map<string, PreparedCampaign[]>;
} => {
	const prepared = prepareCampaigns(data);
	return {
		prepared,
		byStatus: buildCampaignsByStatus(prepared),
	};
};

export const filterCampaignsByStatus = (
	prepared: readonly PreparedCampaign[],
	byStatus: ReadonlyMap<string, PreparedCampaign[]>,
	selectedStatus: string,
): PreparedCampaign[] => {
	if (selectedStatus === "all") {
		return prepared as PreparedCampaign[];
	}

	return byStatus.get(selectedStatus) ?? [];
};

export const filterCampaignsBySearch = (
	items: readonly PreparedCampaign[],
	searchQuery = "",
): PreparedCampaign[] => {
	const q = normalizeSearchQuery(searchQuery);
	if (!q) {
		return items as PreparedCampaign[];
	}

	return items.filter((item) =>
		item._searchFields.some((field) => field.includes(q)),
	);
};

export const sortCampaigns = (
	items: readonly PreparedCampaign[],
	sortInfo: SortInfo,
	pinNew = false,
): PreparedCampaign[] => {
	const sorted = items.slice().sort((a, b) => {
		const { sortBy, sortMethod } = sortInfo;
		let va: string | number = "";
		let vb: string | number = "";

		if (pinNew) {
			if (a._isNew && !b._isNew) return -1;
			if (!a._isNew && b._isNew) return 1;
			/*
			 Обе кампании новые: не сортируем их между собой,
			 сохраняем исходный порядок с сервера (sort стабилен)
			*/
			if (a._isNew && b._isNew) return 0;
		}

		switch (sortBy) {
			case "name":
				va = String(a.campaign_name ?? "");
				vb = String(b.campaign_name ?? "");
				break;
			case "adsId":
				va = a._campaignIdNum;
				vb = b._campaignIdNum;
				break;
			case "app":
				va = String(a.app_id ?? "");
				vb = String(b.app_id ?? "");
				break;
			case "status": {
				const ra = a._statusRank;
				const rb = b._statusRank;
				const aUnknown = ra === undefined;
				const bUnknown = rb === undefined;
				if (aUnknown && bUnknown) {
					const cmp = a._statusLabel.localeCompare(b._statusLabel, undefined, {
						sensitivity: "base",
						numeric: true,
					});
					return sortMethod === "asc" ? cmp : -cmp;
				}
				if (aUnknown) return 1;
				if (bUnknown) return -1;
				const diff = ra - rb;
				return sortMethod === "asc" ? diff : -diff;
			}
			case "cost":
				va = a._costNum;
				vb = b._costNum;
				break;
			default:
				va = String(a.campaign_name ?? "");
				vb = String(b.campaign_name ?? "");
		}

		if (typeof va === "number" && typeof vb === "number") {
			return sortMethod === "asc" ? va - vb : vb - va;
		}

		const cmp = String(va).localeCompare(String(vb), undefined, {
			sensitivity: "base",
			numeric: true,
		});
		return sortMethod === "asc" ? cmp : -cmp;
	});

	return sorted;
};

export const processCampaigns = (
	data: readonly Record<string, unknown>[] | undefined,
	selectedStatus: string,
	sortInfo: SortInfo,
	searchQuery = "",
): PreparedCampaign[] => {
	const { prepared, byStatus } = buildPreparedCampaignsIndex(data);
	const filteredByStatus = filterCampaignsByStatus(
		prepared,
		byStatus,
		selectedStatus,
	);
	const filteredBySearch = filterCampaignsBySearch(
		filteredByStatus,
		searchQuery,
	);
	const pinNew =
		selectedStatus === "all" && normalizeSearchQuery(searchQuery) === "";
	return sortCampaigns(filteredBySearch, sortInfo, pinNew);
};
